import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { BankgiroReplacementNotice as Notice } from "@/services/invoiceGeneration";

/** Shown once after a bank-reported bankgiro replaced one that was typed in (FIN-818 F5). */
export function BankgiroReplacementNotice({ notice, onDismiss }: {
  notice: Notice;
  onDismiss: (bankAccountId: string) => Promise<unknown>;
}) {
  const t = useTranslations("consultantInvoiceCreate.replacement");
  const [dismissing, setDismissing] = useState(false);
  const [failed, setFailed] = useState(false);

  const dismiss = async () => {
    setDismissing(true);
    setFailed(false);
    try {
      await onDismiss(notice.bankAccountId);
    } catch {
      setFailed(true);
    } finally {
      setDismissing(false);
    }
  };

  return (
    <Alert>
      <AlertTitle>{t("title")}</AlertTitle>
      <AlertDescription>
        {t("body", { current: notice.currentNumber, previous: notice.previousNumber, bank: notice.bankName })}
        {failed && <span role="alert" className="block text-destructive">{t("dismissFailed")}</span>}
        <Button type="button" variant="ghost" size="sm" className="mt-1 block px-0" disabled={dismissing} onClick={dismiss}>
          {dismissing && <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />}
          {t("gotIt")}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
